import React, { useEffect, useRef, useState } from "react";

const isBrowser = typeof window !== "undefined";

const debugLogs =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debuglogs') === 'true';

const debugStream =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('debugstream') === 'true';

const extManifest =
    typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('extmanifest')
        : null;

const forcePR =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('forcepr') === 'true';

const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export default function LiveDrm({ live_event, ws }) {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const wsRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const manualCloseRef = useRef(false);

    const [state, setState] = useState({
        isPlaying: false,
        isMuted: false,
        volume: 1,
        isFullscreen: false,
        eventData: live_event.data,
        websocketEndpoint: ws,
    });

    const isMatchPlayerType = (type) => {
        const regex = /^(player|poster)$/;
        const isMatched = regex.test(type);
        if (!isMatched) {
            console.warn('[websocket]playerTypeがフォーマットに沿ってません');
        }
        return isMatched;
    };

    const ensureHttps = (url) => {
        if (!url) return;

        try {
            const u = new URL(url.startsWith("//") ? `https:${url}` : url);
            if (u.protocol !== "https:") u.protocol = "https:";
            return u.toString();
        } catch {
            return `https://${url}`;
        }
    };

    const log = (...args) => {
        if (debugLogs) console.log(...args);
    };

    let onShakaBuffering;

    const liveStream = extManifest
        ? extManifest
        : debugStream
            ? (isSafari
                ? 'https://rm-mock-public.s3.ap-northeast-1.amazonaws.com/drm/hls_encrypted/index.m3u8'
                : 'https://rm-mock-public.s3.ap-northeast-1.amazonaws.com/drm/dash_encrypted/manifest.mpd'
            )
            : (
                isSafari
                    ? ensureHttps(state.eventData.Channel.Custom_drm_hls_live_url)
                    : ensureHttps(state.eventData.Channel.Custom_drm_dash_live_url)
            );

    const src = liveStream;
    const VIDEO_URL = src;

    //WebSocket
    const [playerType, setPlayerType] = useState(state.eventData?.Player_type);

    // UI
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const hideTimeoutRef = useRef(null);
    const [volume, setVolume] = useState(1);
    const [isBuffering, setIsBuffering] = useState(false);

    // DRM
    const SERVER_CERTIFICATE_URL = gon.fairplay_server_certificate_url
    const SERVER_LICENSE_URL = gon.fairplay_server_license_url;
    const WIDEVINE_LICENSE_URL = gon.widevine_license_url;
    const PLAYREADY_LICENSE_URL = gon.playready_license_url;

    const keySystems = {
        widevine: "com.widevine.alpha",
        playready: "com.microsoft.playready",
        fairplay: "com.apple.fps.1_0",
    };

    async function checkDRMSupport() {
        const results = {};
        if (isSafari) {
            const fairplaySupported =
                window.WebKitMediaKeys &&
                typeof WebKitMediaKeys.isTypeSupported === "function" &&
                WebKitMediaKeys.isTypeSupported(
                    "com.apple.fps.1_0",
                    'video/mp4; codecs="avc1.42E01E"'
                );
            results.fairplay = !!fairplaySupported;
        } else {
            try {
                await navigator.requestMediaKeySystemAccess("com.apple.fps.1_0", [
                    {
                        initDataTypes: ["cenc"],
                        videoCapabilities: [
                            { contentType: 'video/mp4; codecs="avc1.42E01E"' },
                        ],
                    },
                ]);
                results.fairplay = true;
            } catch (e) {
                results.fairplay = false;
            }
        }

        // Check other DRM key systems
        for (const [name, keySystem] of Object.entries(keySystems)) {
            if (name === "fairplay") continue;
            try {
                if ("requestMediaKeySystemAccess" in navigator) {
                    await navigator.requestMediaKeySystemAccess(keySystem, [
                        {
                            initDataTypes: ["cenc"],
                            videoCapabilities: [
                                { contentType: 'video/mp4; codecs="avc1.42E01E"' },
                            ],
                        },
                    ]);
                    results[name] = true;
                } else {
                    results[name] = false;
                }
            } catch (e) {
                results[name] = false;
            }
        }

        return results;
    }

    log("SERVER_CERTIFICATE_URL: ", SERVER_CERTIFICATE_URL);
    log("SERVER_LICENSE_URL: ", SERVER_LICENSE_URL);
    log("WIDEVINE_LICENSE_URL: ", WIDEVINE_LICENSE_URL);
    log("PLAYREADY_LICENSE_URL: ", PLAYREADY_LICENSE_URL);
    log("websocket: ", state.websocketEndpoint);

    useEffect(() => {
        if (!isBrowser || !videoRef.current) return;

        let destroyed = false;

        // WebSocket init
        createWS();

        // Button hard reset
        if (playerType !== 'poster') {
            setIsPlaying(false);
        }

        checkDRMSupport().then(results => {

            log("Results: " + JSON.stringify(results));

            if (results.widevine || results.playready) {
                initPlayerWvPr();
            }
            else if (results.fairplay) {
                initPlayerFp();
            } else {
                console.log("[ERROR] DRM not supported. HTTPS or localhost domain is mandatory.");
            }
        });

        // UI
        const video = videoRef.current;
        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onVolumeChange = () => {
            setIsMuted(video.muted);
            setVolume(video.volume);
        };
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        video.addEventListener('volumechange', onVolumeChange);
        const handleFsChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFsChange);
        setIsMuted(video.muted);
        setVolume(video.volume);
        const spinnerStyle = document.createElement('style');
        spinnerStyle.innerHTML = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }`;
        document.head.appendChild(spinnerStyle);


        const shaka = window.shaka;

        if (!shaka) {
            console.error("Shaka is not loaded");

            log(VIDEO_URL);

            return;
        }

        // DRM
        const initPlayerCommon = async () => {
            if (playerRef.current) {
                await playerRef.current.destroy();
                playerRef.current = null;
            }
            shaka.polyfill.installAll();

            if (!shaka.Player.isBrowserSupported()) {
                console.error("Shaka not supported");
                return;
            }

            log(state.eventData);

            if (destroyed || !videoRef.current) return;
        };

        const initPlayerFp = async () => {
            await initPlayerCommon();

            if (playerRef.current) {
                await playerRef.current.destroy();
                playerRef.current = null;
            }

            shaka.polyfill.installAll();

            if (shaka.polyfill.PatchedMediaKeysApple) {
                shaka.polyfill.PatchedMediaKeysApple.install();
            }

            if (!shaka.Player.isBrowserSupported()) {
                console.error('Shaka Player is not supported on this browser.');
                return;
            }

            if (destroyed || !videoRef.current) return;

            const shakaPlayer = new shaka.Player(videoRef.current);
            playerRef.current = shakaPlayer;

            if (!SERVER_CERTIFICATE_URL || !SERVER_LICENSE_URL) {
                console.error('FairPlay DRM URLs not set.');
                return;
            }

            shakaPlayer.configure({
                drm: {
                    servers: {
                        'com.apple.fps': SERVER_LICENSE_URL,
                    },
                    advanced: {
                        'com.apple.fps': {
                            serverCertificateUri: SERVER_CERTIFICATE_URL,
                        },
                    },
                    initDataTransform: initDataTransform,
                },
            });

            shakaPlayer.addEventListener('error', (event) => {
                const err = event.detail;
                console.error('Shaka error', err.code, err);
            });

            const netEngine = shakaPlayer.getNetworkingEngine();
            if (netEngine) {
                netEngine.registerRequestFilter((type, request) => {
                    if (
                        type === shaka.net.NetworkingEngine.RequestType.LICENSE &&
                        request.drmInfo?.keySystem === 'com.apple.fps'
                    ) {
                        const spcData = new Uint8Array(request.body);
                        const base64SpcData =
                            shaka.util.Uint8ArrayUtils.toStandardBase64(spcData);
                        const body = JSON.stringify({ spc: base64SpcData });
                        request.body = new TextEncoder().encode(body);
                        request.headers['Content-Type'] = 'application/json';
                    }
                });

                netEngine.registerResponseFilter((type, response) => {
                    if (
                        type === shaka.net.NetworkingEngine.RequestType.LICENSE &&
                        response.uri?.includes('fairplay')
                    ) {
                        const responseText =
                            shaka.util.StringUtils.fromUTF8(response.data);
                        const responseObj = JSON.parse(responseText);
                        response.data =
                            shaka.util.Uint8ArrayUtils.fromBase64(responseObj.ckc);
                    }
                });
            }
            const onShakaBuffering = (e) => {
                setIsBuffering(e.buffering);
            };

            shakaPlayer.addEventListener('buffering', onShakaBuffering);
            await shakaPlayer.load(VIDEO_URL);

        }
        const initPlayerWvPr = async () => {
            await initPlayerCommon();

            try {
                if (playerRef.current) {
                    await playerRef.current.destroy();
                    playerRef.current = null;
                }

                const shakaPlayer = new shaka.Player(video);
                shakaPlayer.current = shakaPlayer;

                const url = VIDEO_URL;

                log(url);

                if (!url) {
                    console.error("No video URL found");
                    return;
                }
                let drmServers = {};

                if (forcePR) {
                    drmServers = {
                        'com.microsoft.playready': PLAYREADY_LICENSE_URL,
                    };
                    console.log("Forcing PlayReady DRM");
                } else {
                    drmServers = {
                        'com.widevine.alpha': WIDEVINE_LICENSE_URL,
                        'com.microsoft.playready': PLAYREADY_LICENSE_URL,
                    };
                }

                shakaPlayer.configure({
                    drm: {
                        servers: drmServers,
                        advanced: {},
                        initDataTransform: initDataTransform,
                    },
                });

                shakaPlayer.addEventListener('error', (event) => {
                    const err = event.detail;
                    console.error('Shaka error', err.code, err);
                });
                const netEngine = shakaPlayer.getNetworkingEngine();
                netEngine.registerRequestFilter(function (type, request) {
                    if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {

                        log(type, request);

                        if (request.drmInfo.keySystem === 'com.widevine.alpha') {
                            request.headers['Content-Type'] = 'application/octet-stream';
                            request.headers['Accept'] = 'application/octet-stream';
                        }
                    }
                });
                netEngine.registerResponseFilter(function (type, response) {
                    if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {

                        log(type, response);


                    }
                });
                const onShakaBuffering = (e) => {
                    setIsBuffering(e.buffering);
                };

                shakaPlayer.addEventListener('buffering', onShakaBuffering);
                await shakaPlayer.load(url);

                log("Video loaded");


            } catch (e) {
                console.error("Player init error", e);
            }
        };

        function initDataTransform(initData, initDataType, drmInfo) {
            try {
                if (initDataType !== 'skd') return initData;

                const skdUri =
                    shaka.util.StringUtils.fromBytesAutoDetect(initData);

                const contentId = skdUri.substring(6); // remove 'skd://'
                const cert = drmInfo.serverCertificate;

                return shaka.util.FairPlayUtils.initDataTransform(
                    initData,
                    contentId,
                    cert
                );

            } catch (e) {
                console.error('initDataTransform error', e);
                throw e;
            }
        }

        return () => {
            destroyed = true;
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }

            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;

                if (spinnerStyle && spinnerStyle.parentNode) {
                    spinnerStyle.parentNode.removeChild(spinnerStyle);
                }
            }

            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
            const player = playerRef.current;
            if (player) {

                return () => {
                    player.removeEventListener('buffering', onShakaBuffering);
                };
            }
        };
    }, [VIDEO_URL, playerType]);

    // UI
    const togglePlay = () => {
        const video = videoRef.current;
        if (video.paused) {
            video.play();
        } else {
            video.pause();
        }
    };
    const toggleMute = () => {
        const video = videoRef.current;
        video.muted = !video.muted;
        setIsMuted(video.muted);
    };

    const toggleFullscreen = () => {
        const container = videoRef.current?.parentElement;
        if (!container) return;

        if (!document.fullscreenElement) {
            container.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    };

    const handleMouseActivity = () => {
        setShowControls(true);

        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }

        hideTimeoutRef.current = setTimeout(() => {
            setShowControls(false);
        }, 2000);
    };

    const handleVolumeChange = (e) => {
        const video = videoRef.current;
        const newVolume = parseFloat(e.target.value);

        video.volume = newVolume;
        video.muted = newVolume === 0;

        setVolume(newVolume);
        setIsMuted(video.muted);
    };

    const styles = {
        bar: {
            position: 'absolute',
            bottom: 10,
            left: 20,
            right: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(0,0,0,0.6)',
            padding: '6px 12px',
            borderRadius: 8,
            fontSize: "1.5rem",
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: showControls ? 'auto' : 'none',
        },
        button: {
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: 'white',
            fontSize: 20,
            cursor: 'pointer',
            padding: 0,
        },
        icon: {
            width: 24,
            textAlign: 'center',
            display: 'inline-block',
        },
        liveContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: 'white',
            fontWeight: 'bold',
        },
        liveDot: {
            width: 10,
            height: 10,
            background: 'red',
            borderRadius: '50%',
            visibility: isPlaying ? "visible" : "hidden",
        },
        liveText: {
            fontSize: 14,
        },
        spinnerOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: isBuffering ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            zIndex: 10,
        },

        spinner: {
            width: 50,
            height: 50,
            border: '4px solid rgba(255,255,255,0.3)',
            borderTop: '4px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
    };

    // WebSocket
    const createWS = () => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }

        manualCloseRef.current = false;

        if (
            wsRef.current &&
            (wsRef.current.readyState === WebSocket.OPEN ||
                wsRef.current.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        if (wsRef.current) {
            try {
                wsRef.current.close();
            } catch (e) {
                log("Error closing previous WS:", e);
            }
        }

        const ws = new WebSocket(state.websocketEndpoint);
        wsRef.current = ws;

        ws.onopen = () => {
            if (wsRef.current !== ws) return;

            ws.send(JSON.stringify({
                action: "subscribe",
                topic: "player"
            }));
        };

        ws.onmessage = (e) => {
            if (wsRef.current !== ws) return;

            let data;
            try {
                data = JSON.parse(e.data);
            } catch (err) {
                log("Invalid WS message:", e.data);
                return;
            }

            log("Websocket data:", data);

            setPlayerType((prev) => {
                if (
                    isMatchPlayerType(prev) &&
                    data.evuuid === state.eventData?.UUID
                ) {
                    return prev !== data.playerType
                        ? data.playerType
                        : prev;
                }
                return prev;
            });
        };

        ws.onerror = (e) => {
            log("WS error:", e);
        };

        ws.onclose = () => {
            if (wsRef.current === ws) {
                wsRef.current = null;
            }

            if (manualCloseRef.current) return;

            // Avoid multiple reconnect timers
            if (!reconnectTimerRef.current) {
                reconnectTimerRef.current = setTimeout(() => {
                    reconnectTimerRef.current = null;
                    createWS();
                }, 5000);
            }
        };
    };

    if (playerType == "poster") {
        const isPosterUrl = state.eventData?.Poster_url && state.eventData?.Poster_url !== '';
        return (<div
            style={{
                width: '100%',
                height: '100vh',
                background: 'black',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
            }}
        >
            {isPosterUrl ? (
                <img
                    src={state.eventData?.Poster_url}
                    alt='Poster'
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        position: 'absolute',
                    }}
                />
            ) : (
                <h1 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                    準備中です
                </h1>
            )}
        </div>);
    }

    return (

        <div
            id="container_drm"
            onMouseMove={() => {
                handleMouseActivity();
            }}
            onMouseLeave={() => {
                if (!videoRef.current || videoRef.current.paused) return;
                setShowControls(false);
            }}
            style={{
                width: '100%',
                height: '100vh',
                backgroundColor: 'black',
                position: 'relative',
            }}
        >
            <video
                key={playerType}
                ref={videoRef}
                controls={false}
                width="640"
                style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',

                }}
            />
            <div style={styles.spinnerOverlay}>
                <div style={styles.spinner}></div>
            </div>
            <div style={styles.bar}>
                <button onClick={togglePlay} style={styles.button}>
                    {isPlaying ? '\u23F8' : '\u23F5'}
                </button>

                <div style={styles.liveContainer}>
                    <span style={styles.liveDot} />
                    <span style={styles.liveText}>LIVE</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={toggleMute} style={styles.button}>
                        <span style={styles.icon}>
                            {isMuted || volume === 0 ? '🔇' : '🔊'}
                        </span>
                    </button>

                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={handleVolumeChange}
                        style={{ width: 80 }}
                    />

                    <button onClick={toggleFullscreen} style={styles.button}>
                        <span style={styles.icon}>
                            {isFullscreen ? '⤢' : '⛶'}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}